import { SectionLabel, Tags } from "@risalabs_frontend_org/oasis-ui-kit";
import { Fragment } from "react";
import type { AdditionalPayer } from "../../../../data-model/coverageInformation";

/** Props for {@link EvCoverageAdditionalPayersContent}. */
interface EvCoverageAdditionalPayersContentProps {
  payers: AdditionalPayer[];
}

/** A single read-only label / value pair. */
interface LabeledField {
  label: string;
  value: string;
}

/**
 * Section title derived from the payer's billing role.
 *
 * @param role - Display role string (`secondary`, `tertiary`, ...) or `-`
 * @returns `Secondary Payer` / `Tertiary Payer` etc.; `Additional Payer`
 *   when the role is unknown
 */
const toSectionTitle = (role: string): string => {
  if (role === "-") {
    return "Additional Payer";
  }
  return `${role.charAt(0).toUpperCase()}${role.slice(1).toLowerCase()} Payer`;
};

/**
 * First-letter capitalization for field values ("MEDICARE" → "Medicare").
 *
 * @param value - Display value already `"-"` fallbacked by the mapper
 * @returns Capitalized value; missing values (`"-"`) pass through unchanged
 */
const toCapitalizedDisplay = (value: string): string =>
  value === "-"
    ? value
    : value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();

/**
 * Capitalizes the first letter of every word ("AETNA HEALTH INC." →
 * "Aetna Health Inc.").
 *
 * @param value - Display value already `"-"` fallbacked by the mapper
 * @returns Word-capitalized value; missing values (`"-"`) pass through
 */
const toTitleCaseDisplay = (value: string): string =>
  value === "-"
    ? value
    : value
        .toLowerCase()
        .split(/\s+/)
        .map((word) =>
          word.length ? word.charAt(0).toUpperCase() + word.slice(1) : word,
        )
        .join(" ");

/**
 * Uppercases an identifier-style value ("anthbcbs" → "ANTHBCBS").
 *
 * @param value - Display value already `"-"` fallbacked by the mapper
 * @returns Uppercased value; missing values (`"-"`) pass through
 */
const toUppercaseDisplay = (value: string): string =>
  value === "-" ? value : value.toUpperCase();

/**
 * Renders the Type / Status fields as borderless oasis {@link Tags} badges
 * using the specified tag colors (yellow Type, teal Active status, red
 * Inactive status).
 *
 * @param value - Normalized display string (`"true"` / `"false"` / other / `"-"`)
 * @returns The tag, or the raw text when the value is missing (`"-"`)
 */
const StatusTag = ({ value }: { value: string }) => {
  if (value === "-") {
    return <span className="text-primaryGray-6">{value}</span>;
  }
  const isFlag = value === "true" || value === "false";
  const { label, ...tagColors } = isFlag
    ? value === "true"
      ? { label: "Active", backgroundColor: "#e6f3f0", textColor: "#005d49" }
      : { label: "Inactive", backgroundColor: "#ffe8e8", textColor: "#cc0300" }
    : {
        label: toCapitalizedDisplay(value),
        backgroundColor: "#fffcd6",
        textColor: "#665d00",
      };
  return <Tags label={label} {...tagColors} />;
};

/**
 * Read-only value text ("Overline SemiBold 10") for the non-tag fields.
 *
 * @param props - Display value
 * @returns The value rendered as plain text (no box — the fields are read-only)
 */
const ReadOnlyValue = ({ value }: { value: string }) => (
  <span className="text-[10px] font-semibold leading-4 text-primaryGray-1">
    {value}
  </span>
);

/**
 * Read-only card rendering a single additional payer as a titled group of
 * label / value pairs, styled after the Medicaid Payer Plan sections.
 *
 * @param props - Payer display row
 * @returns A bordered section card with a two-column field grid
 */
const AdditionalPayerSection = ({ payer }: { payer: AdditionalPayer }) => {
  const fields: LabeledField[] = [
    { label: "Payer Name", value: toTitleCaseDisplay(payer.name) },
    { label: "Member ID", value: toUppercaseDisplay(payer.memberId) },
    { label: "Type", value: toCapitalizedDisplay(payer.type) },
    { label: "Status", value: payer.active },
    { label: "Plan", value: toUppercaseDisplay(payer.plan) },
    {
      label: "Effective Date",
      value: toCapitalizedDisplay(payer.effectiveDate),
    },
  ];

  const rows: LabeledField[][] = [];
  for (let index = 0; index < fields.length; index += 2) {
    rows.push(fields.slice(index, index + 2));
  }

  return (
    <div className="flex flex-col">
      <SectionLabel
        label={toSectionTitle(payer.role)}
        accentColor="#0056D6"
        className="mb-2 !text-tiny !font-bold !leading-[18px] !tracking-[0.5px] !text-primaryGray-6 sm:mb-3"
      />
      <div className="flex flex-col items-start gap-4 self-stretch rounded-md border border-primaryGray-15 bg-white p-4">
        {rows.map((row, rowIndex) => (
          <Fragment key={row.map((field) => field.label).join("-")}>
            {rowIndex > 0 && (
              <div className="w-full self-stretch border-t border-dashed border-primaryGray-14" />
            )}
            <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-x-8">
              {row.map((field) => (
                <div
                  key={field.label}
                  className="flex flex-col items-start gap-1"
                >
                  <span className="text-[11px] font-bold leading-[18px] text-primaryGray-6">
                    {field.label}
                  </span>
                  {field.label === "Type" || field.label === "Status" ? (
                    <StatusTag value={field.value} />
                  ) : (
                    <ReadOnlyValue value={field.value} />
                  )}
                </div>
              ))}
            </div>
          </Fragment>
        ))}
      </div>
    </div>
  );
};

/**
 * Read-only body for the "Additional Payers" inner tab: one labeled section
 * per other payer reported by the tier's EV, grouped by billing role.
 *
 * The tab is only rendered when at least one payer exists, so `payers` is
 * always non-empty here.
 *
 * @param props - Additional-payer rows (already `"-"` fallbacked by the mapper)
 * @returns The Additional Payers tab body
 */
export const EvCoverageAdditionalPayersContent = ({
  payers,
}: EvCoverageAdditionalPayersContentProps) => (
  <div className="flex min-h-full flex-col gap-4 md:gap-6">
    {payers.map((payer, index) => (
      <AdditionalPayerSection
        key={`${payer.name}-${payer.role}-${index}`}
        payer={payer}
      />
    ))}
  </div>
);

export default EvCoverageAdditionalPayersContent;
